import React from 'react';
import Sheet from '../ui/Sheet';
import Button from '../ui/Button';
import { Check, DownloadCloud } from '../../icons';

const PropertyModal = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  property = null,
  formData,
  setFormData,
  isFetchingApiData,
  onFetchApiData,
  apiError,
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-detect property type when title changes
      if (field === 'apiTitle' && value) {
        const titleLower = value.toLowerCase();
        let forSale = prev.forSale || false;
        let forRent = prev.forRent || false;
        
        // Reset if we're changing the title
        if (titleLower !== prev.apiTitle?.toLowerCase()) {
          forSale = false;
          forRent = false;
        }
        
        // Check for sale indicators
        if (titleLower.includes('for sale') || titleLower.includes('πώληση') || 
            titleLower.includes('sale') || titleLower.includes('προς πώληση') || 
            titleLower.includes('πωλείται')) {
          forSale = true;
        }
        
        // Check for rent indicators
        if (titleLower.includes('for rent') || titleLower.includes('ενοικίαση') || 
            titleLower.includes('rent') || titleLower.includes('rental') ||
            titleLower.includes('προς ενοικίαση') || titleLower.includes('ενοικιάζεται') ||
            titleLower.includes('ενοίκιο')) {
          forRent = true;
        }
        
        // Check last word
        const words = titleLower.trim().split(/\s+/);
        const lastWord = words[words.length - 1];
        if (lastWord === 'sale' || lastWord === 'πώληση') {
          forSale = true;
        }
        if (lastWord === 'rent' || lastWord === 'rental' || lastWord === 'ενοικίαση' || lastWord === 'ενοίκιο') {
          forRent = true;
        }
        
        // Default to sale if nothing detected
        if (!forSale && !forRent) {
          forSale = true;
        }
        
        newData.forSale = forSale;
        newData.forRent = forRent;
      }
      
      return newData;
    });
  };

  const handleUrlChange = (url) => {
    handleInputChange('url', url);

    // Auto-detect from URL patterns when URL changes
    if (url) {
      const urlLower = url.toLowerCase();
      let forSale = false;
      let forRent = false;

      if (urlLower.includes('/real-estate-for-sale/') ||
          urlLower.includes('/property-for-sale/') ||
          urlLower.includes('/houses-for-sale/') ||
          urlLower.includes('/apartments-for-sale/')) {
        forSale = true;
      }

      if (urlLower.includes('/real-estate-for-rent/') ||
          urlLower.includes('/property-for-rent/') ||
          urlLower.includes('/houses-for-rent/') ||
          urlLower.includes('/apartments-for-rent/')) {
        forRent = true;
      }

      if (forSale || forRent) {
        setFormData(prev => ({ ...prev, forSale, forRent }));
      }
    }
  };

  const hasPreview = !!(formData.apiTitle || formData.area || formData.imageURL || formData.latitude);
  const notNA = (v) => v && v !== 'N/A';
  const formId = property ? 'property-update-form' : 'property-add-form';

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      mode="sheet"
      size="md"
      title={title}
      aria-label={title}
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form={formId}>
            {property ? 'Update property' : 'Track property'}
          </Button>
        </div>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="space-y-4">
        {/* URL + fetch */}
        <div>
          <label className="label" htmlFor={`${formId}-url`}>Listing URL *</label>
          <div className="flex gap-2">
            <input
              id={`${formId}-url`}
              type="url"
              value={formData.url || ''}
              onChange={(e) => handleUrlChange(e.target.value)}
              className="input flex-1"
              placeholder="https://www.bazaraki.com/… or https://dom.com.cy/…"
              required
              autoFocus={!property}
            />
            <Button type="button" variant="secondary" onClick={onFetchApiData} disabled={isFetchingApiData || !formData.url} loading={isFetchingApiData}>
              {!isFetchingApiData && <DownloadCloud className="w-4 h-4" />}
              {isFetchingApiData ? 'Fetching' : 'Fetch'}
            </Button>
          </div>
          {apiError && <p className="text-danger text-sm mt-2">{apiError}</p>}
          {!apiError && !hasPreview && (
            <p className="text-xs text-muted mt-2">Paste a listing link and tap Fetch to fill the details automatically, or enter them manually below.</p>
          )}
        </div>

        {/* Fetched Data Preview */}
        {hasPreview && (
          <div className="p-3 bg-page border border-accent/30 rounded-card flex gap-3">
            {formData.imageURL && (
              <img
                src={formData.imageURL}
                alt="Property"
                referrerPolicy="no-referrer"
                className="w-24 h-20 object-cover rounded-lg flex-shrink-0"
                onError={(e) => (e.target.style.display = 'none')}
              />
            )}
            <div className="min-w-0 text-sm space-y-1">
              <div className="flex items-center gap-1.5 text-accent text-xs font-semibold">
                <Check className="w-3.5 h-3.5" /> Listing detected
              </div>
              {formData.apiTitle && <p className="font-medium text-primary leading-snug line-clamp-2">{formData.apiTitle}</p>}
              <p className="text-secondary text-xs">
                {[
                  formData.bedrooms && `${formData.bedrooms} bd`,
                  formData.bathrooms && `${formData.bathrooms} ba`,
                  formData.area && `${formData.area} m²`,
                  notNA(formData.constructionYear) && `built ${formData.constructionYear}`,
                  formData.startPrice && `initial €${parseFloat(formData.startPrice).toLocaleString()}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <p className="text-secondary text-xs">
                {[
                  notNA(formData.pets) && `Pets: ${formData.pets}`,
                  notNA(formData.furnishing) && formData.furnishing,
                  notNA(formData.parking) && `Parking: ${formData.parking}`,
                  notNA(formData.onlineViewing) && `Online view: ${formData.onlineViewing}`,
                  formData.cityId && `City ID: ${formData.cityId}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {formData.latitude && formData.longitude && (
                <a
                  href={`https://maps.google.com/?q=${formData.latitude},${formData.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent text-xs hover:underline"
                >
                  {formData.latitude}, {formData.longitude} (view on map)
                </a>
              )}
              {formData.landlordName && (
                <p className="text-xs text-secondary">
                  Landlord: {formData.landlordName} (Joined: {formData.landlordJoined || 'N/A'}, Ads: {formData.ownerAdCount || 'N/A'})
                </p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor={`${formId}-price`}>Current price (€)</label>
            <input id={`${formId}-price`} type="number" inputMode="numeric" value={formData.price || ''} onChange={(e) => handleInputChange('price', e.target.value)} className="input" placeholder="250000" />
          </div>
          <div>
            <label className="label" htmlFor={`${formId}-start`}>Starting price (€)</label>
            <input id={`${formId}-start`} type="number" inputMode="numeric" value={formData.startPrice || ''} onChange={(e) => handleInputChange('startPrice', e.target.value)} className="input" placeholder="280000" />
          </div>
        </div>

        {/* Property Type */}
        <div>
          <span className="label">Property type</span>
          <div className="flex gap-2">
            {[
              { key: 'forSale', label: 'For Sale' },
              { key: 'forRent', label: 'For Rent' },
            ].map(({ key, label }) => (
              <label
                key={key}
                className={`flex-1 flex items-center justify-center gap-2 h-11 rounded-xl border cursor-pointer text-sm font-medium transition-colors ${
                  formData[key] ? 'bg-accent-soft border-accent text-accent' : 'bg-page border-line text-secondary hover:border-line-strong'
                }`}
              >
                <input type="checkbox" className="sr-only" checked={formData[key] || false} onChange={(e) => handleInputChange(key, e.target.checked)} />
                {formData[key] && <Check className="w-4 h-4" />}
                {label}
              </label>
            ))}
          </div>
          <p className="text-xs text-muted mt-1.5">Select one or both if the property is advertised for both sale and rent.</p>
          {(formData.forSale || formData.forRent) && (
            <p className="text-xs text-accent mt-1">
              Auto-detected {formData.apiTitle ? 'from title' : 'from URL'}: {formData.forSale && formData.forRent ? 'Sale & Rent' : formData.forSale ? 'For Sale' : 'For Rent'}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor={`${formId}-published`}>Published date</label>
            <input id={`${formId}-published`} type="date" value={formData.publishedDate || ''} onChange={(e) => handleInputChange('publishedDate', e.target.value)} className="input" />
          </div>
          <div>
            <label className="label" htmlFor={`${formId}-updated`}>Updated date</label>
            <input id={`${formId}-updated`} type="date" value={formData.updatedDate || ''} onChange={(e) => handleInputChange('updatedDate', e.target.value)} className="input" />
          </div>
        </div>

        <div>
          <label className="label" htmlFor={`${formId}-title`}>Title</label>
          <input id={`${formId}-title`} type="text" value={formData.apiTitle || ''} onChange={(e) => handleInputChange('apiTitle', e.target.value)} className="input" placeholder="Property title" />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label" htmlFor={`${formId}-area`}>Area (m²)</label>
            <input id={`${formId}-area`} type="number" inputMode="numeric" value={formData.area || ''} onChange={(e) => handleInputChange('area', e.target.value)} className="input" placeholder="120" />
          </div>
          <div>
            <label className="label" htmlFor={`${formId}-beds`}>Bedrooms</label>
            <input id={`${formId}-beds`} type="number" inputMode="numeric" value={formData.bedrooms || ''} onChange={(e) => handleInputChange('bedrooms', e.target.value)} className="input" placeholder="3" />
          </div>
          <div>
            <label className="label" htmlFor={`${formId}-baths`}>Bathrooms</label>
            <input id={`${formId}-baths`} type="number" inputMode="numeric" value={formData.bathrooms || ''} onChange={(e) => handleInputChange('bathrooms', e.target.value)} className="input" placeholder="2" />
          </div>
        </div>

        <div>
          <label className="label" htmlFor={`${formId}-image`}>Image URL</label>
          <input id={`${formId}-image`} type="url" value={formData.imageURL || ''} onChange={(e) => handleInputChange('imageURL', e.target.value)} className="input" placeholder="https://…" />
        </div>

        <div>
          <label className="label" htmlFor={`${formId}-notes`}>Notes</label>
          <textarea id={`${formId}-notes`} value={formData.notes || ''} onChange={(e) => handleInputChange('notes', e.target.value)} className="input resize-y min-h-[88px]" rows="3" placeholder="Additional notes…" />
        </div>
      </form>
    </Sheet>
  );
};

export default PropertyModal;
